import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { getPayableAmounts } from "@msuite/utils/incoming-invoices";
import { guardString } from "@msuite/utils/strings";
import type { IncomingInvoiceDetail, Nullable } from "@msuite/utils/types";
import { UserCard } from "@/components/cards";
import { formatInvoiceAmount } from "@/components/incoming-invoices";

/** Props Interface */
interface IncomingInvoiceReductionProps {
	incomingInvoice: Nullable<IncomingInvoiceDetail>;
}

/**
 * An approver's Kürzung, in a card of its own beneath the printed amounts: the net and gross
 * amounts the invoice is paid on, why, and who reduced it — the latest decision that carried the
 * reduced amount. Nothing for an invoice that is not reduced.
 */
export function IncomingInvoiceReduction({ incomingInvoice }: IncomingInvoiceReductionProps) {
	/** Context */
	const { t } = useTranslation();

	/** Computed */
	const payable = incomingInvoice ? getPayableAmounts(incomingInvoice) : null;
	const currency = incomingInvoice?.currency ?? "EUR";
	const decisions = incomingInvoice?.decisions ?? [];
	let reducedBy: (typeof decisions)[number]["user"] = null;
	for (let index = decisions.length - 1; index >= 0; index--) {
		const decision = decisions[index];
		if (decision?.reducedNetAmount === null || decision?.reducedNetAmount === undefined) continue;
		reducedBy = decision.user;
		break;
	}

	/** Guard */
	if (!incomingInvoice || !payable?.isReduced) return null;

	/** Render */
	return (
		<ContentCard
			title={t("incoming_invoices.decision.reductions")}
			icon={icon("Scissors")}
		>
			<DataGrid>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.decision.reduced_net_amount")}</DataGrid.Label>
					<DataGrid.Value>
						{t("incoming_invoices.decision.reduced_from", {
							from: formatInvoiceAmount(incomingInvoice.netAmount, currency),
							to: formatInvoiceAmount(payable.netAmount, currency),
						})}
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.gross_amount")}</DataGrid.Label>
					<DataGrid.Value>
						{t("incoming_invoices.decision.reduced_from", {
							from: formatInvoiceAmount(incomingInvoice.grossAmount, currency),
							to: formatInvoiceAmount(payable.grossAmount, currency),
						})}
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.decision.reduction_reason")}</DataGrid.Label>
					<DataGrid.Value>{guardString(incomingInvoice.reductionReason)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.decision.reduced_by")}</DataGrid.Label>
					<DataGrid.Value>
						{reducedBy ? (
							<UserCard
								userId={reducedBy.id}
								variant="plain"
								fontSize="md"
								withAvatar={false}
							/>
						) : (
							guardString(null)
						)}
					</DataGrid.Value>
				</DataGrid.Row>
			</DataGrid>
		</ContentCard>
	);
}
